import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import type { Polygon } from "geojson";
import React, { useEffect, useRef } from "react";
import { isDev } from "../../../utils";
import type {
  AircraftComponent,
  DoorWall,
  GarageDoor,
  HangarBay,
  HangarDoor,
  ObstaclePlacement,
  Obstruction,
  Placement,
} from "../types";
import type { ExtractionNode } from "../utils/extractionOrder";
import {
  AIRCRAFT_COLORS,
  angleToVector,
  calculateIdealAngleToPoint,
  calculateTowAngle,
  COMPONENT_COLORS,
  DOOR_COLORS,
  geometryToSvgPath,
  getGeometryCentroids,
  getM0ReferencePoint,
  getM0ReferencePointForDoor,
  getM29ReferencePoint,
  getM29ReferencePointForDoor,
  getPolygonCentroid,
  lineStringToSvgPath,
  LOCKED_COLOR,
  M29_DEPTH_COEFFICIENT,
  MOVABLE_OBSTACLE_STYLE,
  OBSTRUCTION_STYLES,
  polygonToSvgPath,
  positionsToSvgPath,
  selectReferencePoint,
  selectReferencePointForDoor,
} from "./hangarCanvasUtils";

interface HangarCanvasProps {
  hangar: {
    width: number;
    depth: number;
    leftDoor?: number; // Width of left door opening from corner
    rightDoor?: number; // Width of right door opening from corner
    /** Garage doors in side/back walls */
    garageDoors?: GarageDoor[];
  };
  placements: Placement[];
  wallMargin?: number;
  /** Dead zones (triangular areas behind door openings) */
  deadZones?: Polygon[];
  showComponents?: boolean;
  onToggleComponents?: (show: boolean) => void;
  showAircraftLabels?: boolean;
  onToggleAircraftLabels?: (show: boolean) => void;
  showHeightLabels?: boolean;
  onToggleHeightLabels?: (show: boolean) => void;
  showTowability?: boolean;
  onToggleTowability?: (show: boolean) => void;
  showMagneticLines?: boolean;
  onToggleMagneticLines?: (show: boolean) => void;
  showDeadZones?: boolean;
  onToggleDeadZones?: (show: boolean) => void;
  /** Static obstructions in the hangar */
  obstructions?: Obstruction[];
  showObstructions?: boolean;
  onToggleObstructions?: (show: boolean) => void;
  /** Movable obstacle placements (GSE, vehicles, etc.) */
  obstaclePlacements?: ObstaclePlacement[];
  showObstaclePlacements?: boolean;
  onToggleObstaclePlacements?: (show: boolean) => void;
  /** Hangar bays with height constraints (stepped-eave hangars) */
  bays?: HangarBay[];
  showBays?: boolean;
  onToggleBays?: (show: boolean) => void;
  /** Show garage door openings */
  showGarageDoors?: boolean;
  onToggleGarageDoors?: (show: boolean) => void;
  selectedAircraftId?: string | null;
  onSelectAircraft?: (id: string | null) => void;
  /** Show animated tow sequence visualization */
  showTowAnimation?: boolean;
  onToggleTowAnimation?: (show: boolean) => void;
  /** Show swept geometry (area covered during tow) */
  showSweptGeometry?: boolean;
  onToggleSweptGeometry?: (show: boolean) => void;
  /** Show tug position at nose gear */
  showTugGeometry?: boolean;
  onToggleShowTugGeometry?: (show: boolean) => void;
  /** Extraction order data for tow animation */
  extractionOrder?: ExtractionNode[];
  /** Debug mode: centroid markers to visualize rotation pivot points */
  debugCentroids?: Array<{ x: number; y: number; label: string }>;
  /** Debug mode: collision pairs to highlight */
  collisionPairs?: Array<{ a: string; b: string; reason: string }>;
  /**
   * Debug rotation for multi-door development (Phase 1).
   * Rotates all hangar content by 0°/90°/180°/270° for visual verification.
   * - 0°: No rotation (south door, default)
   * - 90°: West door becomes front
   * - 180°: North door becomes front
   * - 270°: East door becomes front
   */
  debugRotation?: 0 | 90 | 180 | 270;
  /**
   * Multi-door configuration.
   * When provided, renders doors on all specified walls with color coding.
   */
  doors?: HangarDoor[];
}

/**
 * Render a single component (handles both Polygon and MultiPolygon)
 * Includes height label (top/bottom) at each polygon centroid
 * labelOffset allows shifting labels to avoid collisions
 */
function renderComponent(
  component: AircraftComponent,
  colorKey: keyof typeof COMPONENT_COLORS,
  hangarDepth: number,
  key: string,
  labelOffset: { dx: number; dy: number } = { dx: 0, dy: 0 },
  showHeightLabels: boolean = true
): React.ReactNode {
  if (!component.geom) return null;

  const colors = COMPONENT_COLORS[colorKey];
  const centroids = getGeometryCentroids(component.geom, hangarDepth);
  const heightLabel = `${component.bottom.toFixed(0)}-${component.top.toFixed(
    0
  )}ft`;

  return (
    <g key={key}>
      <path
        d={geometryToSvgPath(component.geom, hangarDepth)}
        fill={colors.fill}
        fillOpacity={0.5}
        stroke={colors.stroke}
        strokeWidth={0.3}
      />
      {showHeightLabels &&
        centroids.map((centroid, i) => (
          <text
            key={`${key}-label-${i}`}
            x={centroid.x + labelOffset.dx}
            y={centroid.y + labelOffset.dy}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={3}
            fill="#666"
          >
            {heightLabel}
          </text>
        ))}
    </g>
  );
}

export const HangarCanvas: React.FC<HangarCanvasProps> = ({
  hangar,
  placements,
  wallMargin = 2,
  deadZones = [],
  showComponents = false,
  onToggleComponents,
  showAircraftLabels = true,
  onToggleAircraftLabels,
  showHeightLabels = true,
  onToggleHeightLabels,
  showTowability = false,
  onToggleTowability,
  showMagneticLines = false,
  onToggleMagneticLines,
  showDeadZones = true,
  onToggleDeadZones,
  obstructions = [],
  showObstructions = true,
  onToggleObstructions,
  obstaclePlacements = [],
  showObstaclePlacements = true,
  onToggleObstaclePlacements,
  bays = [],
  showBays = true,
  onToggleBays,
  showGarageDoors = true,
  onToggleGarageDoors,
  selectedAircraftId,
  onSelectAircraft,
  showTowAnimation = false,
  onToggleTowAnimation,
  showSweptGeometry = false,
  onToggleSweptGeometry,
  showTugGeometry = false,
  onToggleShowTugGeometry,
  extractionOrder = [],
  debugCentroids,
  collisionPairs,
  debugRotation = 0,
  doors = [],
}) => {
  // Performance monitoring (dev-only)
  const renderStartRef = useRef<number>(performance.now());
  renderStartRef.current = performance.now();

  useEffect(() => {
    if (isDev()) {
      const renderTime = performance.now() - renderStartRef.current;
      if (renderTime > 16) {
        // Log if render takes more than 1 frame (16ms)
        console.debug(
          `[HangarCanvas] Render took ${renderTime.toFixed(1)}ms (${
            placements.length
          } aircraft)`
        );
      }
    }
  });

  // Check if we have any door configuration
  const hasLeftDoor = (hangar.leftDoor ?? 0) > 0;
  const hasRightDoor = (hangar.rightDoor ?? 0) > 0;
  const hasDoors = hasLeftDoor || hasRightDoor;
  const garageDoors = hangar.garageDoors ?? [];
  const hasGarageDoors = garageDoors.length > 0;

  // Helper to get exit point for a door (in SVG coordinates)
  const getDoorExitPoint = (doorId: string | undefined): { x: number; y: number } => {
    // Default: south wall center
    const defaultExit = { x: hangar.width / 2, y: hangar.depth };

    if (!doorId || doors.length === 0) return defaultExit;

    const door = doors.find(d => d.id === doorId);
    if (!door) return defaultExit;

    // Calculate exit point based on door wall (in SVG coords where Y=0 is top)
    switch (door.wall) {
      case 'south':
        // South door at SVG bottom (Y = hangar.depth)
        return { x: door.position + door.width / 2, y: hangar.depth };
      case 'north':
        // North door at SVG top (Y = 0)
        return { x: door.position + door.width / 2, y: 0 };
      case 'east':
        // East door at SVG right (X = hangar.width)
        // Y in SVG is flipped, so door.position from bottom of hangar
        return { x: hangar.width, y: hangar.depth - (door.position + door.width / 2) };
      case 'west':
        // West door at SVG left (X = 0)
        return { x: 0, y: hangar.depth - (door.position + door.width / 2) };
      default:
        return defaultExit;
    }
  };

  // Debug rotation: for 90°/270°, swap width/depth in display
  const isRotated90or270 = debugRotation === 90 || debugRotation === 270;
  const effectiveWidth = isRotated90or270 ? hangar.depth : hangar.width;
  const effectiveDepth = isRotated90or270 ? hangar.width : hangar.depth;

  // SVG transform for debug rotation
  // Rotation is around the hangar center, with translation to keep content in view
  const getRotationTransform = (): string => {
    if (debugRotation === 0) return "";

    const cx = hangar.width / 2;
    const cy = hangar.depth / 2;

    if (debugRotation === 90) {
      // Rotate 90° CCW around center, then translate to fit in swapped viewport
      // After rotation, content spans from negative to positive values
      // Need to shift so it starts at (0, 0)
      const dx = (hangar.depth - hangar.width) / 2;
      const dy = (hangar.width - hangar.depth) / 2;
      return `translate(${dx}, ${dy}) rotate(-90, ${cx}, ${cy})`;
    } else if (debugRotation === 180) {
      // Rotate 180° around center
      return `rotate(180, ${cx}, ${cy})`;
    } else if (debugRotation === 270) {
      // Rotate 270° CCW (90° CW) around center
      const dx = (hangar.depth - hangar.width) / 2;
      const dy = (hangar.width - hangar.depth) / 2;
      return `translate(${dx}, ${dy}) rotate(90, ${cx}, ${cy})`;
    }
    return "";
  };

  const rotationTransform = getRotationTransform();

  // Add padding around the hangar (extra bottom padding for door label)
  const padding = 10;
  const bottomPadding = 30;

  // Extra top padding for multi-door to show north door paths
  // North door paths exit at Y > depth, which maps to negative SVG Y
  const isMultiDoor = doors.length > 1;
  const topPadding = isMultiDoor ? 35 : padding;

  const viewBoxWidth = effectiveWidth + padding * 2;
  const viewBoxHeight = effectiveDepth + topPadding + bottomPadding;

  return (
    <Box>
      <svg
        viewBox={`${-padding} ${-topPadding} ${viewBoxWidth} ${viewBoxHeight}`}
        style={{
          width: "100%",
          maxHeight: "500px",
          backgroundColor: "#f5f5f5",
          border: "1px solid #ddd",
        }}
      >
        {/* Debug rotation wrapper - rotates all content for multi-door visualization testing */}
        <g transform={rotationTransform}>
          {/* Hangar outline */}
        <rect
          x={0}
          y={0}
          width={hangar.width}
          height={hangar.depth}
          fill="white"
          stroke="#333"
          strokeWidth={1}
        />

        {/* Grid lines - 10ft intervals */}
        {Array.from({ length: Math.floor(hangar.width / 10) + 1 }, (_, i) => (
          <line
            key={`vgrid-${i}`}
            x1={i * 10}
            y1={0}
            x2={i * 10}
            y2={hangar.depth}
            stroke="#e0e0e0"
            strokeWidth={0.5}
            strokeDasharray={i % 5 === 0 ? undefined : "2,2"}
          />
        ))}
        {Array.from({ length: Math.floor(hangar.depth / 10) + 1 }, (_, i) => (
          <line
            key={`hgrid-${i}`}
            x1={0}
            y1={i * 10}
            x2={hangar.width}
            y2={i * 10}
            stroke="#e0e0e0"
            strokeWidth={0.5}
            strokeDasharray={i % 5 === 0 ? undefined : "2,2"}
          />
        ))}

        {/* Wall margin zone - dashed inner boundary showing safety margin */}
        <rect
          x={wallMargin}
          y={wallMargin}
          width={hangar.width - wallMargin * 2}
          height={hangar.depth - wallMargin * 2}
          fill="none"
          stroke="#999"
          strokeWidth={0.5}
          strokeDasharray="3,3"
        />

        {/* Bay height zones - different ceiling heights (stepped-eave hangars) */}
        {showBays &&
          bays.map((bay) => {
            const centroid = getPolygonCentroid(bay.geom, hangar.depth);
            return (
              <g key={`bay-${bay.id}`}>
                <path
                  d={polygonToSvgPath(bay.geom, hangar.depth)}
                  fill="rgba(33, 150, 243, 0.08)"
                  stroke="#2196F3"
                  strokeWidth={0.5}
                  strokeDasharray="4,2"
                />
                <text
                  x={centroid.x}
                  y={centroid.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={4}
                  fill="#1565C0"
                  fontWeight="bold"
                >
                  {bay.id}
                </text>
                <text
                  x={centroid.x}
                  y={centroid.y + 5}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={3}
                  fill="#1565C0"
                >
                  Clear: {bay.clearHeight}ft
                </text>
              </g>
            );
          })}

        {/* Dead zones - triangular areas behind door openings where aircraft can't be placed */}
        {showDeadZones &&
          deadZones.map((zone, i) => (
            <path
              key={`deadzone-${i}`}
              d={polygonToSvgPath(zone, hangar.depth)}
              fill="rgba(244, 67, 54, 0.25)"
              stroke="#F44336"
              strokeWidth={0.5}
              strokeDasharray="3,2"
            />
          ))}

        {/* Obstructions - static obstacles like pillars, equipment, etc. */}
        {showObstructions &&
          obstructions.map((obs) => {
            const style = OBSTRUCTION_STYLES[obs.type ?? "OBSTRUCTION"];
            const centroid = getPolygonCentroid(obs.geom, hangar.depth);
            return (
              <g key={`obstruction-${obs.id}`}>
                <path
                  d={polygonToSvgPath(obs.geom, hangar.depth)}
                  fill={style.fill}
                  stroke={style.stroke}
                  strokeWidth={0.8}
                  strokeDasharray={style.dasharray}
                />
                {/* Obstruction label with height */}
                <text
                  x={centroid.x}
                  y={centroid.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={3}
                  fill={style.stroke}
                  fontWeight="bold"
                >
                  {obs.label || obs.id}
                </text>
                <text
                  x={centroid.x}
                  y={centroid.y + 4}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={2.5}
                  fill={style.stroke}
                >
                  {obs.height}ft
                </text>
              </g>
            );
          })}

        {/* Movable Obstacles - GSE, vehicles, carts, etc. */}
        {showObstaclePlacements &&
          obstaclePlacements.map((obstacle) => {
            const centroid = getPolygonCentroid(obstacle.geometry, hangar.depth);
            return (
              <g key={`obstacle-${obstacle.id}`}>
                <path
                  d={polygonToSvgPath(obstacle.geometry, hangar.depth)}
                  fill={MOVABLE_OBSTACLE_STYLE.fill}
                  stroke={MOVABLE_OBSTACLE_STYLE.stroke}
                  strokeWidth={MOVABLE_OBSTACLE_STYLE.strokeWidth}
                  strokeDasharray={MOVABLE_OBSTACLE_STYLE.dasharray}
                />
                {/* Obstacle label */}
                <text
                  x={centroid.x}
                  y={centroid.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={3}
                  fill={MOVABLE_OBSTACLE_STYLE.labelColor}
                  fontWeight="bold"
                >
                  {obstacle.label || obstacle.id}
                </text>
                {/* Height label */}
                <text
                  x={centroid.x}
                  y={centroid.y + 4}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={2.5}
                  fill={MOVABLE_OBSTACLE_STYLE.labelColor}
                >
                  {obstacle.height}ft
                </text>
              </g>
            );
          })}

        {/* Door opening visualization */}
        {hasDoors ? (
          <>
            {/* Left door opening (from left corner) */}
            {hasLeftDoor && (
              <line
                x1={0}
                y1={hangar.depth}
                x2={hangar.leftDoor!}
                y2={hangar.depth}
                stroke="#4CAF50"
                strokeWidth={3}
              />
            )}
            {/* Right door opening (from right corner) */}
            {hasRightDoor && (
              <line
                x1={hangar.width - hangar.rightDoor!}
                y1={hangar.depth}
                x2={hangar.width}
                y2={hangar.depth}
                stroke="#4CAF50"
                strokeWidth={3}
              />
            )}
            {/* Wall segments (between doors) */}
            <line
              x1={hangar.leftDoor ?? 0}
              y1={hangar.depth}
              x2={hangar.width - (hangar.rightDoor ?? 0)}
              y2={hangar.depth}
              stroke="#333"
              strokeWidth={2}
            />
            {/* Door opening labels */}
            {hasLeftDoor && (
              <text
                x={(hangar.leftDoor ?? 0) / 2}
                y={hangar.depth + 8}
                textAnchor="middle"
                fontSize={4}
                fill="#4CAF50"
              >
                L Door {hangar.leftDoor}ft
              </text>
            )}
            {hasRightDoor && (
              <text
                x={hangar.width - (hangar.rightDoor ?? 0) / 2}
                y={hangar.depth + 8}
                textAnchor="middle"
                fontSize={4}
                fill="#4CAF50"
              >
                R Door {hangar.rightDoor}ft
              </text>
            )}
          </>
        ) : (
          <>
            {/* Full door opening indicator - subtle dashed line (no doors configured) */}
            <line
              x1={0}
              y1={hangar.depth}
              x2={hangar.width}
              y2={hangar.depth}
              stroke="#4CAF50"
              strokeWidth={1.5}
              strokeDasharray="3,2"
            />
            <text
              x={hangar.width / 2}
              y={hangar.depth + 8}
              textAnchor="middle"
              fontSize={5}
              fill="#4CAF50"
            >
              Door Opening
            </text>
          </>
        )}

        {/* Multi-door openings on all walls */}
        {doors.length > 0 &&
          doors.map((door) => {
            let x1: number, y1: number, x2: number, y2: number;
            let labelX: number, labelY: number;
            let labelAngle = 0;

            // SVG Y-axis is inverted: y=0 at top, y=depth at bottom
            // Hangar coords: Y=0 is front (south), Y=depth is back (north)
            switch (door.wall) {
              case "south":
                // Front wall at Y=0 (SVG bottom = hangar.depth)
                x1 = door.position;
                x2 = door.position + door.width;
                y1 = hangar.depth;
                y2 = hangar.depth;
                labelX = (x1 + x2) / 2;
                labelY = hangar.depth; // Center on the line
                break;
              case "north":
                // Back wall at Y=depth (SVG top = 0)
                x1 = door.position;
                x2 = door.position + door.width;
                y1 = 0;
                y2 = 0;
                labelX = (x1 + x2) / 2;
                labelY = 0; // Center on the line
                break;
              case "east":
                // Right wall at X=width
                x1 = hangar.width;
                x2 = hangar.width;
                y1 = hangar.depth - door.position;
                y2 = hangar.depth - (door.position + door.width);
                labelX = hangar.width;
                labelY = (y1 + y2) / 2;
                labelAngle = 90;
                break;
              case "west":
                // Left wall at X=0
                x1 = 0;
                x2 = 0;
                y1 = hangar.depth - door.position;
                y2 = hangar.depth - (door.position + door.width);
                labelX = 0;
                labelY = (y1 + y2) / 2;
                labelAngle = -90;
                break;
            }

            const doorColor = DOOR_COLORS[door.wall];

            return (
              <g key={`multi-door-${door.id}`}>
                {/* Door opening - thick colored line */}
                <line
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={doorColor}
                  strokeWidth={6}
                  strokeLinecap="round"
                />
                {/* Door label - inlaid in the door bar */}
                <text
                  x={labelX}
                  y={labelY}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={3}
                  fill="#fff"
                  fontWeight="bold"
                  transform={
                    labelAngle !== 0
                      ? `rotate(${labelAngle}, ${labelX}, ${labelY})`
                      : undefined
                  }
                >
                  {door.id} ({door.width}ft)
                </text>
              </g>
            );
          })}

        {/* Garage door openings on back/side walls */}
        {showGarageDoors &&
          garageDoors.map((door, i) => {
            // Calculate line endpoints based on wall type
            // SVG: y=0 at top (BACK wall), y=depth at bottom (FRONT door)
            // Hangar coords: Y=0 is front door, Y=depth is back wall
            // SVG uses flipped Y: svg_y = depth - hangar_y
            let x1: number, y1: number, x2: number, y2: number;
            let labelX: number, labelY: number;
            let labelAngle = 0;

            switch (door.wall) {
              case "back":
                // Back wall is at hangar Y=depth, which is SVG y=0 (top of canvas)
                x1 = door.position;
                x2 = door.position + door.width;
                y1 = 0; // SVG coordinates - top edge (back wall at hangar Y=depth)
                y2 = 0;
                labelX = (x1 + x2) / 2;
                labelY = -5; // Label above the back wall
                break;
              case "left":
                // Left wall at X=0
                x1 = 0;
                x2 = 0;
                y1 = hangar.depth - door.position; // SVG coordinates (flipped)
                y2 = hangar.depth - (door.position + door.width);
                labelX = -3;
                labelY = (y1 + y2) / 2;
                labelAngle = -90;
                break;
              case "right":
                // Right wall at X=width
                x1 = hangar.width;
                x2 = hangar.width;
                y1 = hangar.depth - door.position; // SVG coordinates (flipped)
                y2 = hangar.depth - (door.position + door.width);
                labelX = hangar.width + 3;
                labelY = (y1 + y2) / 2;
                labelAngle = 90;
                break;
            }

            return (
              <g key={`garage-door-${i}`}>
                <line
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke="#8BC34A"
                  strokeWidth={3}
                />
                <text
                  x={labelX}
                  y={labelY}
                  textAnchor="middle"
                  fontSize={3}
                  fill="#689F38"
                  transform={
                    labelAngle !== 0
                      ? `rotate(${labelAngle}, ${labelX}, ${labelY})`
                      : undefined
                  }
                >
                  {door.width}ft
                </text>
              </g>
            );
          })}

        {/* Towability visualization (warp geometry and egress corridor) - render behind aircraft */}
        {showTowability &&
          placements.map((placement, index) => {
            const isSelected = selectedAircraftId === placement.aircraft_id;
            const towability = placement.towability;
            if (!towability?.warpGeometry) return null;

            return (
              <g key={`towability-${placement.aircraft_id}-${index}`}>
                {/* Full egress corridor (from aircraft to door) - rendered first, behind warp */}
                {towability.egressCorridor && (
                  <path
                    d={polygonToSvgPath(
                      towability.egressCorridor,
                      hangar.depth
                    )}
                    fill={
                      isSelected
                        ? "rgba(255, 193, 7, 0.08)"
                        : "rgba(144, 202, 249, 0.08)"
                    }
                    stroke={isSelected ? "#FFA000" : "#90CAF9"}
                    strokeWidth={0.3}
                    strokeDasharray="4,2"
                  />
                )}
                {/* Warp geometry (short-range immediate towing path) */}
                <path
                  d={polygonToSvgPath(towability.warpGeometry, hangar.depth)}
                  fill={isSelected ? "#FFC107" : "#E3F2FD"}
                  fillOpacity={isSelected ? 0.4 : 0.2}
                  stroke={isSelected ? "#FFA000" : "#90CAF9"}
                  strokeWidth={isSelected ? 1 : 0.5}
                  strokeDasharray="2,1"
                />
                {/* Wingtip egress rays */}
                {towability.wingtipRays?.map((ray, rayIndex) => (
                  <path
                    key={`ray-${placement.aircraft_id}-${rayIndex}`}
                    d={lineStringToSvgPath(ray, hangar.depth)}
                    fill="none"
                    stroke={isSelected ? "#FF5722" : "#BBDEFB"}
                    strokeWidth={isSelected ? 1 : 0.5}
                    strokeDasharray="1,1"
                  />
                ))}
              </g>
            );
          })}

        {/* Tug geometry visualization - render behind aircraft */}
        {showTugGeometry &&
          placements.map((placement, index) => {
            const tugGeom = placement.towability?.tugGeometry;
            const noseGear = placement.towability?.noseGearPosition;
            if (!tugGeom) return null;

            const isSelected = selectedAircraftId === placement.aircraft_id;
            const hasTugCollision = !!placement.towability?.tugCollisionReason;

            // Red if collision, orange if selected, purple otherwise
            const fillColor = hasTugCollision
              ? "rgba(244, 67, 54, 0.3)" // Red - collision
              : isSelected
              ? "rgba(255, 152, 0, 0.4)" // Orange - selected
              : "rgba(156, 39, 176, 0.25)"; // Purple - normal
            const strokeColor = hasTugCollision
              ? "#F44336"
              : isSelected
              ? "#FF9800"
              : "#9C27B0";

            return (
              <g key={`tug-${placement.aircraft_id}-${index}`}>
                {/* Tug rectangle */}
                <path
                  d={polygonToSvgPath(tugGeom, hangar.depth)}
                  fill={fillColor}
                  stroke={strokeColor}
                  strokeWidth={isSelected ? 2 : 1}
                />
                {/* Nose gear position marker */}
                {noseGear && (
                  <circle
                    cx={noseGear[0]}
                    cy={hangar.depth - noseGear[1]}
                    r={1.5}
                    fill="#FF5722"
                    stroke="#BF360C"
                    strokeWidth={0.5}
                  />
                )}
              </g>
            );
          })}

        {/* Magnetic lines visualization - render behind aircraft */}
        {showMagneticLines &&
          (() => {
            // Dual reference point system (m0 and m29)
            const m0Point = getM0ReferencePoint(hangar);
            const m29Point = getM29ReferencePoint(hangar);
            const m0SvgY = hangar.depth - m0Point.y; // Y=0, so svgY = depth
            const m29SvgY = hangar.depth - m29Point.y; // Far below door
            const rayLength = 20; // feet

            // Colors: m0 = pink (near doors/walls), m29 = blue (main body)
            const M0_COLOR = "#FF4081";
            const M0_STROKE = "#C2185B";
            const M29_COLOR = "#2196F3";
            const M29_STROKE = "#1565C0";

            return (
              <g>
                {/* Arrow marker definitions - one for each reference type */}
                <defs>
                  <marker
                    id="arrow-m0"
                    markerWidth={4}
                    markerHeight={4}
                    refX={3}
                    refY={2}
                    orient="auto"
                    markerUnits="strokeWidth"
                  >
                    <path d="M0,0 L4,2 L0,4 L1,2 Z" fill={M0_STROKE} />
                  </marker>
                  <marker
                    id="arrow-m29"
                    markerWidth={4}
                    markerHeight={4}
                    refX={3}
                    refY={2}
                    orient="auto"
                    markerUnits="strokeWidth"
                  >
                    <path d="M0,0 L4,2 L0,4 L1,2 Z" fill={M29_STROKE} />
                  </marker>
                  <marker
                    id="arrow-current"
                    markerWidth={4}
                    markerHeight={4}
                    refX={3}
                    refY={2}
                    orient="auto"
                    markerUnits="strokeWidth"
                  >
                    <path d="M0,0 L4,2 L0,4 L1,2 Z" fill="#424242" />
                  </marker>
                </defs>

                {/* Reference point indicators - per-door for multi-door, or single for legacy */}
                {doors.length > 1 ? (
                  // Multi-door: show m0 indicator at each door with door-specific colors
                  doors.map((door) => {
                    const doorM0 = getM0ReferencePointForDoor(door, hangar);
                    const doorColor = DOOR_COLORS[door.wall];
                    // Calculate SVG position for m0 indicator
                    let indicatorX = doorM0.x;
                    let indicatorSvgY = hangar.depth - doorM0.y;
                    // Offset indicator outside the hangar boundary
                    let labelOffsetX = 0;
                    let labelOffsetY = 0;
                    switch (door.wall) {
                      case 'south':
                        indicatorSvgY = hangar.depth + 8;
                        labelOffsetY = 5;
                        break;
                      case 'north':
                        indicatorSvgY = -8;
                        labelOffsetY = -2;
                        break;
                      case 'east':
                        indicatorX = hangar.width + 8;
                        labelOffsetX = 5;
                        break;
                      case 'west':
                        indicatorX = -8;
                        labelOffsetX = -5;
                        break;
                    }
                    return (
                      <g key={`m0-${door.id}`}>
                        <circle
                          cx={indicatorX}
                          cy={indicatorSvgY}
                          r={2.5}
                          fill={doorColor}
                          stroke="white"
                          strokeWidth={0.5}
                        />
                        <text
                          x={indicatorX + labelOffsetX}
                          y={indicatorSvgY + labelOffsetY + 6}
                          textAnchor="middle"
                          fontSize={3}
                          fill={doorColor}
                          fontWeight="bold"
                        >
                          m0
                        </text>
                      </g>
                    );
                  })
                ) : (
                  // Single-door: show legacy m0/m29 indicators for south door
                  <>
                    {/* m0 reference point (door line) - pink */}
                    <circle
                      cx={m0Point.x}
                      cy={m0SvgY}
                      r={3}
                      fill={M0_COLOR}
                      stroke={M0_STROKE}
                      strokeWidth={1}
                    />
                    <text
                      x={m0Point.x + 8}
                      y={m0SvgY + 2}
                      textAnchor="start"
                      fontSize={4}
                      fill={M0_STROKE}
                      fontWeight="bold"
                    >
                      m0 (door)
                    </text>

                    {/* m29 reference point (deep) - blue, render below viewBox so show indicator line */}
                    <line
                      x1={m29Point.x}
                      y1={m0SvgY}
                      x2={m29Point.x}
                      y2={m0SvgY + 15}
                      stroke={M29_COLOR}
                      strokeWidth={1}
                      strokeDasharray="2,2"
                      opacity={0.5}
                    />
                    <circle
                      cx={m29Point.x}
                      cy={m0SvgY + 15}
                      r={3}
                      fill={M29_COLOR}
                      stroke={M29_STROKE}
                      strokeWidth={1}
                    />
                    <text
                      x={m29Point.x + 8}
                      y={m0SvgY + 17}
                      textAnchor="start"
                      fontSize={4}
                      fill={M29_STROKE}
                      fontWeight="bold"
                    >
                      m29 (deep: -
                      {(M29_DEPTH_COEFFICIENT * hangar.depth).toFixed(0)}ft)
                    </text>
                  </>
                )}

                {/* Rays for each aircraft - color-coded by which ref point is used */}
                {placements.map((placement, index) => {
                  // Get aircraft center from geometry centroid
                  const centroid = getPolygonCentroid(
                    placement.geometry,
                    hangar.depth
                  );
                  const cx = centroid.x;
                  const svgCy = centroid.y;
                  // Convert SVG Y back to world Y for calculations
                  const worldCy = hangar.depth - svgCy;

                  // Position-aware reference point selection
                  // Use door-relative selection if placement has a doorId
                  const assignedDoor = placement.doorId
                    ? doors.find(d => d.id === placement.doorId)
                    : undefined;

                  const refSelection = assignedDoor
                    ? selectReferencePointForDoor(
                        { x: cx, y: worldCy },
                        assignedDoor,
                        hangar
                      )
                    : selectReferencePoint(
                        { x: cx, y: worldCy },
                        hangar
                      );
                  const refPoint = refSelection.point;
                  const refType = refSelection.type;
                  // Use door color for multi-door, or m0/m29 colors for single-door
                  const arrowColor = assignedDoor && doors.length > 1
                    ? DOOR_COLORS[assignedDoor.wall]
                    : refType === "m0" ? M0_STROKE : M29_STROKE;
                  const arrowMarker =
                    refType === "m0" ? "url(#arrow-m0)" : "url(#arrow-m29)";

                  // Ideal angle ray (toward selected reference point)
                  const idealAngle = calculateIdealAngleToPoint(
                    { x: cx, y: worldCy },
                    refPoint
                  );
                  const idealDir = angleToVector(idealAngle);
                  const idealEndX = cx + idealDir.x * rayLength;
                  const idealEndSvgY =
                    hangar.depth - (worldCy + idealDir.y * rayLength);

                  // Current tow direction ray (nose or tail, whichever points toward door)
                  const towAngle = calculateTowAngle(placement.angle);
                  const currentDir = angleToVector(towAngle);
                  const currentEndX = cx + currentDir.x * rayLength;
                  const currentEndSvgY =
                    hangar.depth - (worldCy + currentDir.y * rayLength);

                  return (
                    <g key={`magnetic-${placement.aircraft_id}-${index}`}>
                      {/* Ideal angle arrow (color-coded by ref type) */}
                      <line
                        x1={cx}
                        y1={svgCy}
                        x2={idealEndX}
                        y2={idealEndSvgY}
                        stroke={arrowColor}
                        strokeWidth={1.5}
                        markerEnd={arrowMarker}
                      />
                      {/* Current heading arrow (dark grey dashed) */}
                      <line
                        x1={cx}
                        y1={svgCy}
                        x2={currentEndX}
                        y2={currentEndSvgY}
                        stroke="#424242"
                        strokeWidth={1.5}
                        strokeDasharray="4,2"
                        markerEnd="url(#arrow-current)"
                      />
                      {/* Small indicator showing which ref is used */}
                      <circle
                        cx={cx}
                        cy={svgCy}
                        r={2}
                        fill={assignedDoor && doors.length > 1
                          ? DOOR_COLORS[assignedDoor.wall]
                          : refType === "m0" ? M0_COLOR : M29_COLOR}
                        stroke="white"
                        strokeWidth={0.5}
                      />
                    </g>
                  );
                })}
              </g>
            );
          })()}

        {/* Aircraft placements */}
        {placements.map((placement, index) => {
          // Use door color if placement has doorId, otherwise fall back to aircraft colors
          const assignedDoor = placement.doorId
            ? doors.find((d) => d.id === placement.doorId)
            : undefined;
          const color = assignedDoor
            ? DOOR_COLORS[assignedDoor.wall]
            : AIRCRAFT_COLORS[index % AIRCRAFT_COLORS.length];
          const isSelected = selectedAircraftId === placement.aircraft_id;
          const isLocked = placement.isLocked;
          // Use main geometry centroid for label placement
          // (body centroid can be incorrect for multi-door rotated placements)
          const centroid = getPolygonCentroid(placement.geometry, hangar.depth);
          const hasComponents = showComponents && placement.components;

          return (
            <g
              key={`${placement.aircraft_id}-${index}`}
              style={{ cursor: onSelectAircraft ? "pointer" : undefined }}
              onClick={() =>
                onSelectAircraft?.(isSelected ? null : placement.aircraft_id)
              }
            >
              {/* Locked indicator - gold dashed border (rendered first, behind aircraft) */}
              {isLocked && (
                <path
                  d={polygonToSvgPath(placement.geometry, hangar.depth)}
                  fill="none"
                  stroke={LOCKED_COLOR}
                  strokeWidth={2}
                  strokeDasharray="4,2"
                />
              )}

              {/* Aircraft shape (dimmed when showing components, highlighted when selected) */}
              <path
                d={polygonToSvgPath(placement.geometry, hangar.depth)}
                fill={hasComponents ? "#e0e0e0" : color}
                fillOpacity={hasComponents ? 0.3 : 0.6}
                stroke={isSelected ? "#FF5722" : hasComponents ? "#999" : color}
                strokeWidth={isSelected ? 1.5 : 0.5}
              />

              {/* Component parts (when enabled) */}
              {hasComponents && placement.components && (
                <>
                  {renderComponent(
                    placement.components.body,
                    "body",
                    hangar.depth,
                    `${placement.aircraft_id}-${index}-body`,
                    { dx: 0, dy: 0 },
                    showHeightLabels
                  )}
                  {renderComponent(
                    placement.components.tail,
                    "tail",
                    hangar.depth,
                    `${placement.aircraft_id}-${index}-tail`,
                    { dx: -6, dy: 0 },
                    showHeightLabels
                  )}
                  {renderComponent(
                    placement.components.wing,
                    "wing",
                    hangar.depth,
                    `${placement.aircraft_id}-${index}-wing`,
                    { dx: 0, dy: 0 },
                    showHeightLabels
                  )}
                  {renderComponent(
                    placement.components.verticalTailFin,
                    "verticalTailFin",
                    hangar.depth,
                    `${placement.aircraft_id}-${index}-vtf`,
                    { dx: 6, dy: 0 },
                    showHeightLabels
                  )}
                </>
              )}

              {/* Aircraft model label with angle, lock indicator, and door assignment */}
              {showAircraftLabels && (
                <>
                  <text
                    x={centroid.x}
                    y={centroid.y - 2}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={4}
                    fill="#000"
                    fontWeight="bold"
                  >
                    {isLocked ? "L " : ""}
                    {placement.aircraft.model.length > 25
                      ? `${placement.aircraft.model.slice(0, 25)}…`
                      : placement.aircraft.model}{" "}
                    {placement.angle !== 0
                      ? `(${Math.round(placement.angle)}°)`
                      : ""}
                  </text>
                  {/* Door assignment indicator */}
                  {assignedDoor && (
                    <text
                      x={centroid.x}
                      y={centroid.y + 3}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize={3}
                      fill={DOOR_COLORS[assignedDoor.wall]}
                      fontWeight="bold"
                    >
                      → {assignedDoor.id}
                    </text>
                  )}
                </>
              )}
            </g>
          );
        })}

        {/* Swept Geometry Layer - render behind tow paths */}
        {showSweptGeometry && extractionOrder.length > 0 && (
          <g className="swept-geometry-layer">
            {extractionOrder.map((node) => {
              if (!node.curvedPath?.sweptGeometry) return null;
              const placement = placements[node.placementIndex];
              const isSelected = selectedAircraftId === node.aircraft_id;
              const isCycle = node.extractionOrder === -1;

              // Get door-specific color for multi-door scenarios
              const assignedDoor = placement?.doorId
                ? doors.find((d) => d.id === placement.doorId)
                : undefined;
              const doorColor = assignedDoor && doors.length > 1
                ? DOOR_COLORS[assignedDoor.wall]
                : "#4CAF50"; // Default green for single-door

              const fillColor = isCycle
                ? "rgba(244, 67, 54, 0.15)"
                : isSelected
                ? "rgba(255, 152, 0, 0.25)"
                : `${doorColor}1F`; // Door color with 12% opacity
              const strokeColor = isCycle
                ? "#F44336"
                : isSelected
                ? "#FF9800"
                : doorColor;

              return (
                <path
                  key={`swept-${node.placementIndex}`}
                  d={polygonToSvgPath(
                    node.curvedPath.sweptGeometry,
                    hangar.depth
                  )}
                  fill={fillColor}
                  stroke={strokeColor}
                  strokeWidth={isSelected ? 1 : 0.5}
                  strokeDasharray="4,2"
                />
              );
            })}
          </g>
        )}

        {/* Tow Animation Layer */}
        {showTowAnimation && extractionOrder.length > 0 && (
          <g className="tow-animation-layer">
            <defs>
              <style>{`
                @keyframes drawTowPath {
                  from { stroke-dashoffset: 500; }
                  to { stroke-dashoffset: 0; }
                }
                @keyframes fadeInTow {
                  from { opacity: 0; }
                  to { opacity: 1; }
                }
                .tow-path-animated {
                  stroke-dasharray: 500;
                  animation: drawTowPath 0.5s ease-out forwards;
                }
                .tow-badge-animated {
                  animation: fadeInTow 0.3s ease-out forwards;
                }
              `}</style>
            </defs>
            {extractionOrder
              .slice()
              .sort((a, b) => {
                // Valid orders first, then cycles
                if (a.extractionOrder === -1 && b.extractionOrder === -1)
                  return 0;
                if (a.extractionOrder === -1) return 1;
                if (b.extractionOrder === -1) return -1;
                return a.extractionOrder - b.extractionOrder;
              })
              .map((node, idx) => {
                const placement = placements[node.placementIndex];
                if (!placement) return null;

                const centroid = getPolygonCentroid(
                  placement.geometry,
                  hangar.depth
                );
                const isCycle = node.extractionOrder === -1;

                // Get door-specific color for multi-door scenarios
                const assignedDoor = placement.doorId
                  ? doors.find((d) => d.id === placement.doorId)
                  : undefined;
                const color = isCycle
                  ? "#F44336" // Red for cycles
                  : assignedDoor && doors.length > 1
                    ? DOOR_COLORS[assignedDoor.wall] // Door color for multi-door
                    : "#4CAF50"; // Default green for single-door

                // Use curved path if available (server now generates correct paths for all doors)
                const hasCurvedPath =
                  node.curvedPath?.centerPath &&
                  node.curvedPath.centerPath.length >= 2;

                // Get exit point based on aircraft's assigned door (fallback for missing curved path)
                const exitPoint = getDoorExitPoint(placement.doorId);

                // Nose gear position (start of curved path)
                const noseGearPos = hasCurvedPath
                  ? node.curvedPath!.centerPath[0]
                  : null;

                return (
                  <g key={`tow-anim-${node.placementIndex}`}>
                    {/* Nose gear marker - shows where algorithm thinks nose is */}
                    {noseGearPos && (
                      <circle
                        cx={noseGearPos[0]}
                        cy={hangar.depth - noseGearPos[1]}
                        r={3}
                        fill="#FF5722"
                        stroke="#BF360C"
                        strokeWidth={1}
                      />
                    )}
                    {/* Animated path to door - curved or straight */}
                    {hasCurvedPath ? (
                      <path
                        d={positionsToSvgPath(
                          node.curvedPath!.centerPath,
                          hangar.depth
                        )}
                        fill="none"
                        stroke={color}
                        strokeWidth={1.5}
                        strokeOpacity={0.7}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="tow-path-animated"
                        style={{ animationDelay: `${idx * 0.3}s` }}
                      />
                    ) : (
                      <line
                        x1={centroid.x}
                        y1={centroid.y}
                        x2={exitPoint.x}
                        y2={exitPoint.y}
                        stroke={color}
                        strokeWidth={1.5}
                        strokeOpacity={0.7}
                        className="tow-path-animated"
                        style={{ animationDelay: `${idx * 0.3}s` }}
                      />
                    )}
                    {/* Order number badge */}
                    <circle
                      cx={centroid.x}
                      cy={centroid.y}
                      r={6}
                      fill={color}
                      className="tow-badge-animated"
                      style={{
                        opacity: 0,
                        animationDelay: `${idx * 0.3}s`,
                      }}
                    />
                    <text
                      x={centroid.x}
                      y={centroid.y + 0.5}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize={4}
                      fill="white"
                      fontWeight="bold"
                      className="tow-badge-animated"
                      style={{
                        opacity: 0,
                        animationDelay: `${idx * 0.3}s`,
                      }}
                    >
                      {isCycle ? "!" : node.extractionOrder}
                    </text>
                  </g>
                );
              })}
          </g>
        )}

        {/* Dimension labels */}
        <text
          x={hangar.width / 2}
          y={hangar.depth + 22}
          textAnchor="middle"
          fontSize={6}
          fill="#999"
        >
          {hangar.width} ft
        </text>
        <text
          x={-6}
          y={hangar.depth / 2}
          textAnchor="middle"
          fontSize={6}
          fill="#999"
          transform={`rotate(-90, -6, ${hangar.depth / 2})`}
        >
          {hangar.depth} ft
        </text>

        {/* Corner coordinate labels for debugging */}
        <text x={3} y={8} fontSize={4} fill="#666" fontFamily="monospace">
          (0, {hangar.depth})
        </text>
        <text
          x={hangar.width - 3}
          y={8}
          textAnchor="end"
          fontSize={4}
          fill="#666"
          fontFamily="monospace"
        >
          ({hangar.width}, {hangar.depth})
        </text>
        <text
          x={3}
          y={hangar.depth - 3}
          fontSize={4}
          fill="#666"
          fontFamily="monospace"
        >
          (0, 0)
        </text>
        <text
          x={hangar.width - 3}
          y={hangar.depth - 3}
          textAnchor="end"
          fontSize={4}
          fill="#666"
          fontFamily="monospace"
        >
          ({hangar.width}, 0)
        </text>
        <text
          x={hangar.width / 2}
          y={5}
          textAnchor="middle"
          fontSize={3}
          fill="#999"
        >
          BACK WALL (high Y)
        </text>
        <text
          x={hangar.width / 2}
          y={hangar.depth - 2}
          textAnchor="middle"
          fontSize={3}
          fill="#999"
        >
          DOOR (Y=0)
        </text>

        {/* Debug Mode: Centroid markers */}
        {debugCentroids &&
          debugCentroids.map((centroid, idx) => {
            const svgY = hangar.depth - centroid.y;
            return (
              <g key={`debug-centroid-${idx}`}>
                {/* Crosshair marker */}
                <line
                  x1={centroid.x - 3}
                  y1={svgY}
                  x2={centroid.x + 3}
                  y2={svgY}
                  stroke="#E91E63"
                  strokeWidth={1.5}
                />
                <line
                  x1={centroid.x}
                  y1={svgY - 3}
                  x2={centroid.x}
                  y2={svgY + 3}
                  stroke="#E91E63"
                  strokeWidth={1.5}
                />
                <circle cx={centroid.x} cy={svgY} r={2} fill="#E91E63" />
                {/* Label */}
                <text
                  x={centroid.x + 5}
                  y={svgY - 3}
                  fontSize={4}
                  fill="#E91E63"
                  fontWeight="bold"
                >
                  {centroid.label} pivot
                </text>
              </g>
            );
          })}

        {/* Debug Mode: Collision indicator */}
        {collisionPairs && collisionPairs.length > 0 && (
          <g>
            <rect
              x={hangar.width - 50}
              y={2}
              width={48}
              height={8 + collisionPairs.length * 5}
              fill="rgba(244, 67, 54, 0.9)"
              rx={2}
            />
            <text
              x={hangar.width - 48}
              y={8}
              fontSize={4}
              fill="white"
              fontWeight="bold"
            >
              COLLISION!
            </text>
            {collisionPairs.slice(0, 4).map((pair, idx) => (
              <text
                key={idx}
                x={hangar.width - 48}
                y={13 + idx * 5}
                fontSize={3}
                fill="white"
              >
                {pair.a}↔{pair.b}
              </text>
            ))}
            {collisionPairs.length > 4 && (
              <text
                x={hangar.width - 48}
                y={13 + 4 * 5}
                fontSize={3}
                fill="white"
              >
                +{collisionPairs.length - 4} more...
              </text>
            )}
          </g>
        )}
        </g>
        {/* End debug rotation wrapper */}

        {/* Debug rotation indicator */}
        {debugRotation !== 0 && (
          <g>
            <rect
              x={-padding + 2}
              y={-padding + 2}
              width={55}
              height={12}
              fill="rgba(255, 152, 0, 0.9)"
              rx={2}
            />
            <text
              x={-padding + 5}
              y={-padding + 10}
              fontSize={6}
              fill="white"
              fontWeight="bold"
            >
              Rot: {debugRotation}°
            </text>
          </g>
        )}
      </svg>

      {/* Controls */}
      <Box sx={{ mt: 1 }}>
        {onToggleComponents && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showComponents}
                onChange={(e) => onToggleComponents(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption">
                Show component parts (wing, tail, body)
              </Typography>
            }
          />
        )}
        {onToggleAircraftLabels && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showAircraftLabels}
                onChange={(e) => onToggleAircraftLabels(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption">Show aircraft model</Typography>
            }
          />
        )}
        {onToggleHeightLabels && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showHeightLabels}
                onChange={(e) => onToggleHeightLabels(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption">Show height labels</Typography>
            }
          />
        )}
        {onToggleTowability && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showTowability}
                onChange={(e) => onToggleTowability(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption">
                Show towability (egress paths)
              </Typography>
            }
          />
        )}
        {onToggleMagneticLines && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showMagneticLines}
                onChange={(e) => onToggleMagneticLines(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption">
                Show magnetic lines (ideal angles)
              </Typography>
            }
          />
        )}
        {onToggleDeadZones && hasDoors && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showDeadZones}
                onChange={(e) => onToggleDeadZones(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption">
                Show dead zones (no-place areas)
              </Typography>
            }
          />
        )}
        {onToggleObstructions && obstructions.length > 0 && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showObstructions}
                onChange={(e) => onToggleObstructions(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption">
                Show obstructions ({obstructions.length})
              </Typography>
            }
          />
        )}
        {onToggleObstaclePlacements && obstaclePlacements.length > 0 && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showObstaclePlacements}
                onChange={(e) => onToggleObstaclePlacements(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption">
                Show movable obstacles ({obstaclePlacements.length})
              </Typography>
            }
          />
        )}
        {onToggleBays && bays.length > 0 && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showBays}
                onChange={(e) => onToggleBays(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption">
                Show bay heights ({bays.length})
              </Typography>
            }
          />
        )}
        {onToggleGarageDoors && hasGarageDoors && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showGarageDoors}
                onChange={(e) => onToggleGarageDoors(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption">
                Show garage doors ({garageDoors.length})
              </Typography>
            }
          />
        )}
        {onToggleTowAnimation && placements.length > 0 && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showTowAnimation}
                onChange={(e) => onToggleTowAnimation(e.target.checked)}
              />
            }
            label={
              <Typography variant="caption">
                Show tow sequence animation
              </Typography>
            }
          />
        )}
        {onToggleSweptGeometry &&
          extractionOrder.some((n) => n.curvedPath?.sweptGeometry) && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showSweptGeometry}
                  onChange={(e) => onToggleSweptGeometry(e.target.checked)}
                />
              }
              label={
                <Typography variant="caption">
                  Show swept geometry (tow path area)
                </Typography>
              }
            />
          )}
        {onToggleShowTugGeometry &&
          placements.some((p) => p.towability?.tugGeometry) && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showTugGeometry}
                  onChange={(e) => onToggleShowTugGeometry(e.target.checked)}
                />
              }
              label={
                <Typography variant="caption">Show tug position</Typography>
              }
            />
          )}
      </Box>

      {/* Legend */}
      <Box sx={{ mt: 1, display: "flex", gap: 2, flexWrap: "wrap" }}>
        {showComponents ? (
          // Component legend
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: COMPONENT_COLORS.wing.fill,
                  border: `1px solid ${COMPONENT_COLORS.wing.stroke}`,
                }}
              />
              <Typography variant="caption">Wing</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: COMPONENT_COLORS.tail.fill,
                  border: `1px solid ${COMPONENT_COLORS.tail.stroke}`,
                }}
              />
              <Typography variant="caption">Tail</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: COMPONENT_COLORS.body.fill,
                  border: `1px solid ${COMPONENT_COLORS.body.stroke}`,
                }}
              />
              <Typography variant="caption">Body</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: COMPONENT_COLORS.verticalTailFin.fill,
                  border: `1px solid ${COMPONENT_COLORS.verticalTailFin.stroke}`,
                }}
              />
              <Typography variant="caption">Vertical Fin</Typography>
            </Box>
          </>
        ) : (
          // Aircraft legend
          placements.map((placement, index) => (
            <Box
              key={`${placement.aircraft_id}-${index}`}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor:
                    AIRCRAFT_COLORS[index % AIRCRAFT_COLORS.length],
                  opacity: 0.6,
                  border: `1px solid ${
                    AIRCRAFT_COLORS[index % AIRCRAFT_COLORS.length]
                  }`,
                }}
              />
              <Typography variant="caption">
                {placement.aircraft.make} {placement.aircraft.model}
              </Typography>
            </Box>
          ))
        )}
        {/* Locked aircraft legend */}
        {placements.some((p) => p.isLocked) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                backgroundColor: "transparent",
                border: `2px dashed ${LOCKED_COLOR}`,
              }}
            />
            <Typography variant="caption">Locked Position</Typography>
          </Box>
        )}
        {/* Movable obstacles legend */}
        {showObstaclePlacements && obstaclePlacements.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                backgroundColor: MOVABLE_OBSTACLE_STYLE.fill,
                border: `1px dashed ${MOVABLE_OBSTACLE_STYLE.stroke}`,
              }}
            />
            <Typography variant="caption">Movable Obstacles (GSE)</Typography>
          </Box>
        )}
        {/* Towability legend */}
        {showTowability && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: "rgba(144, 202, 249, 0.08)",
                  border: "1px dashed #90CAF9",
                }}
              />
              <Typography variant="caption">Egress Corridor</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: "#E3F2FD",
                  border: "1px dashed #90CAF9",
                }}
              />
              <Typography variant="caption">Warp Path</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 2,
                  backgroundColor: "#BBDEFB",
                  borderTop: "1px dashed #BBDEFB",
                }}
              />
              <Typography variant="caption">Wingtip Rays</Typography>
            </Box>
          </>
        )}
        {/* Swept geometry legend */}
        {showSweptGeometry && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: "rgba(76, 175, 80, 0.12)",
                  border: "1px dashed #4CAF50",
                }}
              />
              <Typography variant="caption">Swept Area</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 2,
                  backgroundColor: "#4CAF50",
                }}
              />
              <Typography variant="caption">Curved Tow Path</Typography>
            </Box>
          </>
        )}
        {/* Tug geometry legend */}
        {showTugGeometry && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: "rgba(156, 39, 176, 0.25)",
                  border: "1px solid #9C27B0",
                }}
              />
              <Typography variant="caption">Tug Position</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#FF5722",
                  border: "1px solid #BF360C",
                }}
              />
              <Typography variant="caption">Nose Gear</Typography>
            </Box>
          </>
        )}
        {/* Magnetic lines legend */}
        {showMagneticLines && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#FF4081",
                }}
              />
              <Typography variant="caption">m0 (near doors/walls)</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#2196F3",
                }}
              />
              <Typography variant="caption">m29 (main body)</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 2,
                  backgroundColor: "#C2185B",
                }}
              />
              <Typography variant="caption">Ideal Angle (m0)</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 2,
                  backgroundColor: "#1565C0",
                }}
              />
              <Typography variant="caption">Ideal Angle (m29)</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 0,
                  borderTop: "2px dashed #424242",
                }}
              />
              <Typography variant="caption">Current Heading</Typography>
            </Box>
          </>
        )}
        {/* Door and dead zone legend */}
        {hasDoors && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 3,
                  backgroundColor: "#4CAF50",
                }}
              />
              <Typography variant="caption">Door Opening</Typography>
            </Box>
            {showDeadZones && deadZones.length > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: "rgba(244, 67, 54, 0.25)",
                    border: "1px dashed #F44336",
                  }}
                />
                <Typography variant="caption">Dead Zone (no-place)</Typography>
              </Box>
            )}
          </>
        )}
        {/* Obstruction legend */}
        {showObstructions && obstructions.length > 0 && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: OBSTRUCTION_STYLES.OBSTRUCTION.fill,
                  border: `1px solid ${OBSTRUCTION_STYLES.OBSTRUCTION.stroke}`,
                }}
              />
              <Typography variant="caption">Obstruction</Typography>
            </Box>
            {obstructions.some((o) => o.type === "NO_TOW_AREA") && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: OBSTRUCTION_STYLES.NO_TOW_AREA.fill,
                    border: `1px dashed ${OBSTRUCTION_STYLES.NO_TOW_AREA.stroke}`,
                  }}
                />
                <Typography variant="caption">No-Tow Area</Typography>
              </Box>
            )}
            {obstructions.some((o) => o.type === "CAUTION") && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: OBSTRUCTION_STYLES.CAUTION.fill,
                    border: `1px dashed ${OBSTRUCTION_STYLES.CAUTION.stroke}`,
                  }}
                />
                <Typography variant="caption">Caution Zone</Typography>
              </Box>
            )}
          </>
        )}
        {/* Bay height zone legend */}
        {showBays && bays.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                backgroundColor: "rgba(33, 150, 243, 0.08)",
                border: "1px dashed #2196F3",
              }}
            />
            <Typography variant="caption">Bay Height Zone</Typography>
          </Box>
        )}
        {/* Garage door legend */}
        {showGarageDoors && hasGarageDoors && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 3,
                backgroundColor: "#8BC34A",
              }}
            />
            <Typography variant="caption">Garage Door Opening</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
